import type { AdoptResult, AdoptSkipReason } from 'config-hub-server/src/service/adopt.js';
import { ENTITY_TYPES, type EntityType } from './entityTypes.js';
import { fieldSpec } from './fields.js';
import { countOf } from './labels.js';
import { countWord, listOf } from './pluralise.js';

/**
 * What an adopt asks for. One shape for every caller — the Environments card, the empty
 * state, the drawer's "Adopt from <env>", its field diff and the inbox — so the confirmation
 * sentence and the procedure dispatch are written once (`hooks/useAdopt.tsx`).
 *
 * `records` is named records of one type (`ENTITY_TYPES[type].adoptMany`); `fields` keeps just
 * those per-environment fields as the environment's own, instead of copying the whole record.
 */
export type AdoptRequest = { environment: string; count: number } & (
    | { scope: 'all' }
    | { scope: 'records'; type: EntityType; keys: string[]; fields?: string[] }
    | { scope: 'studio-games'; studio: string }
);

const hasFields = (
    request: AdoptRequest,
): request is Extract<AdoptRequest, { scope: 'records' }> & { fields: string[] } =>
    request.scope === 'records' && request.fields !== undefined;

/** The noun phrase of one request: "2 studios and the games under them", "3 games", "UI URL and Status of 1 game", … */
const describeAdopt = (request: AdoptRequest): string => {
    if (hasFields(request)) {
        return `${listOf(request.fields.map((f) => fieldSpec(request.type, f).label))} of ${countOf(request.type, request.keys.length)}`;
    }
    switch (request.scope) {
        case 'all':
            return countWord(request.count, 'record');
        case 'records':
            return ENTITY_TYPES[request.type].adoptNoun(request.keys.length);
        case 'studio-games':
            return `every game under ${request.studio}`;
    }
};

/** The noun phrases of several requests joined: "2 studios and the games under them, and 1 game". */
export const describeAdoptList = (requests: AdoptRequest[]): string => requests.map(describeAdopt).join(', and ');

/**
 * Fields adopted into overrides (**Adopt as override**) land as the environment's own values,
 * not in master's default: the confirm must not call that "into master".
 */
export const adoptsAsOverride = (requests: AdoptRequest[]): boolean => requests.length > 0 && requests.every(hasFields);

/** The adopt confirm's title. */
export const adoptTitle = (requests: AdoptRequest[]): string =>
    adoptsAsOverride(requests) ? 'Adopt as override' : 'Adopt into master';

/** Several requests to one environment as one sentence: "Adopt X, and Y from dev"; `null` for none — nothing renders until something is ticked. */
export const describeAdopts = (requests: AdoptRequest[]): string | null =>
    requests.length === 0 ? null : `Adopt ${describeAdoptList(requests)} from ${requests[0]!.environment}`;

/**
 * Why an adopt wrote nothing, from the server's skip reasons, when they all agree. Keyed by the
 * server's own `AdoptSkipReason`, so a reworded reason fails to compile here; the rest (an
 * untargeted record) read fine as sent.
 */
const SKIP_REASONS: Partial<Record<AdoptSkipReason, string>> = {
    'already in master': 'master already has it',
    'no per-environment field differs': 'what differs is decided by master for every environment — push it instead',
    'chosen fields already match': 'the chosen fields already match',
};

/**
 * What the confirmation says the adopt does. Records named one by one keep the environment's
 * values as overrides for it when master already has them; the whole-environment adopts leave those alone.
 */
/** Chosen fields land as `env == <name>` overrides, never as master's value — say so. */
const fieldsEffect = (requests: AdoptRequest[]): string => {
    const one = requests.filter(hasFields).flatMap((r) => r.fields).length === 1;
    const environment = requests[0]!.environment;
    return `${one ? `It is saved as a ${environment} override` : `They are saved as ${environment} overrides`} — master's value, and every other environment, stay as they are.`;
};

export const adoptEffect = (requests: AdoptRequest[]): string =>
    requests.some(hasFields)
        ? fieldsEffect(requests)
        : requests.some((r) => r.scope === 'records')
          ? "Master gets a copy of each one it doesn't have. For each one it has, this environment's per-environment values are saved as overrides for it — master's value, and every other environment, stay as they are."
          : 'Master gets a copy of each one as this environment last reported it. Records already in master are left as they are.';

/** The toast after an adopt: what happened, and whether anything did. */
export const adoptMessage = (result: AdoptResult): { text: string; done: boolean } => {
    const copied = result.created.length + result.updated.length;
    const own = result.environmentValues.length;
    const skipped = result.skipped.length;
    if (copied === 0 && own === 0) {
        const reasons = [...new Set(result.skipped.map((s) => s.reason))];
        const why = reasons.length === 1 ? (SKIP_REASONS[reasons[0]!] ?? reasons[0]!) : 'master already matches it';
        return { text: `Nothing to adopt from ${result.environment}: ${why}`, done: false };
    }
    const parts = [
        ...(copied > 0 ? [`Adopted ${countWord(copied, 'record')} from ${result.environment}`] : []),
        ...(own > 0
            ? [
                  `${copied > 0 ? '' : 'Adopted '}${result.environment}'s values as ${result.environment} overrides for ${countWord(own, 'record')}`,
              ]
            : []),
    ];
    return { text: parts.join(', and ') + (skipped > 0 ? ` · ${skipped} unchanged` : ''), done: true };
};
